import Constants from '@/Constants';
import {
    FirstAccessRequest,
    ForgotPasswordRequest,
    ImportHistoryRequest,
    Person,
    PersonPostPutRequest,
    PersonRequest,
    UserActiveRequest
} from '@/models/Person';
import http from '@/services/HttpService';
import { toast } from 'react-toastify';
import { call, put, select } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { setPersonDynamicAttributes, updateMyPerson as updateMyIdentityPerson } from '../identity/action';
import { ApplicationState, store } from '../index';
import {
    activeUserFailure,
    activeUserSuccess,
    firstAccessFailure,
    firstAccessSuccess,
    forgotPasswordFailure,
    forgotPasswordSuccess,
    getImportDetailFailure,
    getImportDetailSuccess,
    getImportHistoryFailure,
    getImportHistorySuccess,
    getPeopleFailure,
    getPeopleSuccess,
    getPersonFailure,
    getPersonSuccess,
    getPublicPersonFailure,
    getPublicPersonSuccess,
    getTotalActivePeopleFailure,
    getTotalActivePeopleSuccess,
    getTotalRegisteredPeopleSuccess,
    handlePersonFailure,
    postPersonSuccess,
    putPersonSuccess,
    updateMyPersonSuccess
} from './action';
import { ListViewPeopleData, PeopleTypes } from './types';

export function* getPeople(action: PayloadAction<PeopleTypes.GET_PEOPLE_REQUEST, PersonRequest>) {
    try {
        const responsePerson = yield call(http.get, Constants.PEOPLE_URI, { params: action.payload });
        const listOfPeople = { list: responsePerson.data, total: parseInt(responsePerson.headers.total) } as ListViewPeopleData;
        const personIds = responsePerson.data.map((person: Person) => person.id).join(',');

        let listOfUser = [];
        if (responsePerson.data.length > 0) {
            const responseUser = yield call(http.get, `${Constants.USER_URI}?personIds=${personIds}`);
            listOfUser = responseUser.data;
        }

        yield put(getPeopleSuccess({ listOfUser, listOfPeople }));
    } catch (err) {
        yield put(getPeopleFailure(err));
    }
}

export function* getPerson(action: PayloadAction<PeopleTypes.GET_PERSON_REQUEST, { personId: number; personOnly: boolean }>) {
    try {
        const { personId, personOnly } = action.payload;
        const { data: person } = yield call(http.get, `${Constants.PEOPLE_URI}/${personId}`);

        if (personOnly) {
            yield put(getPersonSuccess({ person }));
        } else {
            const personIds = person.id;
            const { data: users } = yield call(http.get, `${Constants.USER_URI}?personIds=${personIds}`);
            yield put(
                getPersonSuccess({
                    person,
                    user: users[0],
                }),
            );
        }
    } catch (err) {
        yield put(getPersonFailure(err));
    }
}

export function* postPerson(action: PayloadAction<PeopleTypes.POST_PERSON_REQUEST, PersonPostPutRequest>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { person: _person, history } = action.payload;
        const { data: person } = yield call(http.post, Constants.PEOPLE_URI, _person);
        const personIds = person.id;
        const { data: users } = yield call(http.get, `${Constants.USER_URI}?personIds=${personIds}`);
        yield put(postPersonSuccess({ person, user: users[0] }));
        history.push('/people');
        toast.success(toastMessages.PERSON_SAVE_SUCCESS);
    } catch (err) {
        yield put(handlePersonFailure(err));
    }
}

export function* putPerson(action: PayloadAction<PeopleTypes.PUT_PERSON_REQUEST, PersonPostPutRequest>) {
    const {
        locale: {
            data: { toastMessages },
        },
    } = store.getState();
    try {
        const { person, history, willClose } = action.payload;
        yield call(http.put, `${Constants.PEOPLE_URI}/${person.id}`, person);
        yield put(putPersonSuccess());
        if (person.dynamicAttributes) {
            yield put(setPersonDynamicAttributes(person.dynamicAttributes));
        }
        if (willClose) {
            history.push('/people');
            toast.success(toastMessages.PERSON_SAVE_SUCCESS);
        }
    } catch (err) {
        yield put(handlePersonFailure(err));
    }
}

export function* activeUser(action: PayloadAction<PeopleTypes.ACTIVE_REQUEST, UserActiveRequest>) {
    try {
        yield call(http.post, `${Constants.USER_URI}/${action.payload.userId}/activate`, {
            applicationId: action.payload.applicationId,
            activate: action.payload.activate,
        });

        yield call(getTotalActivePeople, {
            type: PeopleTypes.GET_TOTAL_ACTIVE_PEOPLE_REQUEST,
            payload: {
                offset: 0,
                limit: 0,
                orgId: action.payload.orgId,
                applicationId: action.payload.applicationId,
                orderBy: Constants.USER_ORDER_BY_NAME,
                onlyActive: true,
                search: '',
            } as PersonRequest,
        });

        yield put(activeUserSuccess(action.payload));
    } catch (err) {
        yield put(activeUserFailure(err));
    }
}

export function* firstAccess(action: PayloadAction<PeopleTypes.FIRST_ACCESS_REQUEST, FirstAccessRequest>) {
    try {
        yield call(http.post, `${Constants.PEOPLE_URI}/${action.payload.personId}/first-access-event`, {});
        yield put(firstAccessSuccess());
    } catch (err) {
        yield put(firstAccessFailure(err));
    }
}

export function* forgotPassword(action: PayloadAction<PeopleTypes.FORGOT_PASSWORD_REQUEST, ForgotPasswordRequest>) {
    try {
        yield call(http.post, `${Constants.USER_URI}/forgot-password`, action.payload);
        yield put(forgotPasswordSuccess());
    } catch (err) {
        yield put(forgotPasswordFailure(err));
    }
}

export function* getImportHistory(action: PayloadAction<PeopleTypes.GET_IMPORT_HISTORY_REQUEST, ImportHistoryRequest>) {
    const {
        identity: {
            data: {
                user: { orgId, applicationId },
            },
        },
        locale: {
            data: { toastMessages },
        },
    } = store.getState();

    try {
        const { data } = yield call(
            http.get,
            `${Constants.USER_IMPORT_URI}?applicationId=${applicationId}&organizationId=${orgId}&limit=${action.payload.limit}&offset=${action.payload.offset}`,
        );
        yield put(getImportHistorySuccess(data));
    } catch (err) {
        toast.error(toastMessages.IMPORT_HISTORY_ERROR);
        yield put(getImportHistoryFailure(err));
    }
}

export function* getImportDetail(
    action: PayloadAction<
        PeopleTypes.GET_IMPORT_DETAIL_REQUEST,
        { id: number; limit: number; offset: number; success: boolean; error: boolean; search: string }
    >,
) {
    try {
        const { search, error, success, limit, offset, id } = action.payload;

        let email = '';
        if (search && search.length > 0) email = search;

        let status = '';
        if (success) status = 'COMPLETED';
        if (error) status = 'COMPLETED_WITH_ERRORS';
        if (success && error) status = '';

        let filterParams = email !== '' ? '&email=' + email : '';
        filterParams += status !== '' ? '&status=' + status : '';

        const pagination = `?offset=${offset ? offset : 0}&limit=${limit ? limit : 100}`;
        const response = yield call(http.get, `${Constants.USER_IMPORT_URI}/${id}/results${pagination}${filterParams}`);

        yield put(getImportDetailSuccess({ data: response.data, total: parseInt(response.headers.total) }));
    } catch (err) {
        yield put(getImportDetailFailure(err));
    }
}

export function* getTotalActivePeople(action: PayloadAction<PeopleTypes.GET_TOTAL_ACTIVE_PEOPLE_REQUEST, PersonRequest>) {
    try {
        const { onlyActive } = action.payload;
        const response = yield call(http.get, Constants.PEOPLE_URI, { params: action.payload });
        if (onlyActive) {
            yield put(getTotalActivePeopleSuccess(response.headers.total as number));
        } else {
            yield put(getTotalRegisteredPeopleSuccess(response.headers.total as number));
        }
    } catch (err) {
        yield put(getTotalActivePeopleFailure(err));
    }
}

export function* updateMyPerson() {
    try {
        const personId: number = yield select((state: ApplicationState) => state.identity.data.person.id);
        const { data } = yield call(http.get, `${Constants.PEOPLE_URI}/${personId}`);
        yield put(updateMyPersonSuccess(data));
        yield put(updateMyIdentityPerson(data));
    } catch (err) {
        yield put(getPersonFailure(err));
    }
}

export function* getPublicPersonRequest(action: PayloadAction<PeopleTypes.GET_PUBLIC_PERSON_REQUEST, number>) {
    try {
        const { data } = yield call(http.get, `${Constants.PEOPLE_URI}/${action.payload}?withoutPrivateData=true`);
        yield put(getPublicPersonSuccess(data));
    } catch (err) {
        yield put(getPublicPersonFailure(err));
    }
}
